import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { workflowAPI } from '../services/api';
import { WorkflowApproval } from '../types';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Eye,
  FileText,
  Users,
  Settings,
  Plus
} from 'lucide-react';

const WorkflowManagement: React.FC = () => {
  const [selectedApproval, setSelectedApproval] = useState<WorkflowApproval | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [comments, setComments] = useState('');

  const { data: pendingApprovals, loading, refetch } = useApi<WorkflowApproval[]>(
    () => workflowAPI.getPendingApprovals(),
    []
  );

  const handleProcessApproval = async (action: 'approved' | 'rejected') => {
    if (!selectedApproval) return;

    setIsProcessing(true);
    try {
      await workflowAPI.processApproval(selectedApproval.id, action, comments);
      setShowApprovalModal(false);
      setSelectedApproval(null);
      setComments('');
      refetch();
    } catch (error) {
      console.error('Error processing approval:', error);
      alert('Error processing approval. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'vendor_invoice':
        return <FileText className="h-5 w-5" />;
      case 'purchase_order':
        return <FileText className="h-5 w-5" />;
      case 'payment':
        return <FileText className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Workflow Management</h1>
        <div className="flex items-center space-x-4">
          <button className="btn-secondary">
            <Settings className="h-4 w-4 mr-2" />
            Workflow Rules
          </button>
          <button className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Create Rule
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
              <p className="text-2xl font-bold text-gray-900">
                {pendingApprovals?.length || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Approved Today</p>
              <p className="text-2xl font-bold text-gray-900">0</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Rejected Today</p>
              <p className="text-2xl font-bold text-gray-900">0</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Users className="h-6 w-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Workflows</p>
              <p className="text-2xl font-bold text-gray-900">0</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Approvals */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Pending Approvals</h2>
        </div>

        <div className="p-6">
          {pendingApprovals && pendingApprovals.length > 0 ? (
            <div className="space-y-4">
              {pendingApprovals.map((approval) => (
                <div key={approval.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {getEntityIcon(approval.entity_type || '')}
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {approval.workflow_name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {approval.entity_type?.replace('_', ' ').toUpperCase()}: {approval.entity_reference}
                        </p>
                        <p className="text-sm text-gray-500">
                          Submitted by: {approval.first_name} {approval.last_name}
                        </p>
                        {approval.amount && (
                          <p className="text-sm font-medium text-gray-900">
                            Amount: ${approval.amount.toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(approval.status)}`}>
                        {approval.status.toUpperCase()}
                      </span>
                      
                      <button
                        onClick={() => {
                          setSelectedApproval(approval);
                          setShowApprovalModal(true);
                        }}
                        className="p-2 text-gray-600 hover:text-primary-600"
                        title="Review Approval"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No pending approvals.</p>
            </div>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedApproval && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Review Approval Request
              </h3>
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedApproval(null);
                  setComments('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Request Details:</h4>
                <div className="space-y-2">
                  <div className="flex">
                    <span className="font-medium w-1/3">Workflow:</span>
                    <span className="w-2/3">{selectedApproval.workflow_name}</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium w-1/3">Entity Type:</span>
                    <span className="w-2/3">{selectedApproval.entity_type?.replace('_', ' ').toUpperCase()}</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium w-1/3">Reference:</span>
                    <span className="w-2/3">{selectedApproval.entity_reference}</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium w-1/3">Submitted By:</span>
                    <span className="w-2/3">{selectedApproval.first_name} {selectedApproval.last_name}</span>
                  </div>
                  {selectedApproval.amount && (
                    <div className="flex">
                      <span className="font-medium w-1/3">Amount:</span>
                      <span className="w-2/3">${selectedApproval.amount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex">
                    <span className="font-medium w-1/3">Level:</span>
                    <span className="w-2/3">{selectedApproval.level}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comments (Optional)
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={3}
                  className="input-field"
                  placeholder="Add any comments about your decision..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedApproval(null);
                    setComments('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleProcessApproval('rejected')}
                  disabled={isProcessing}
                  className="btn-secondary"
                >
                  {isProcessing ? 'Rejecting...' : 'Reject'}
                </button>
                <button
                  onClick={() => handleProcessApproval('approved')}
                  disabled={isProcessing}
                  className="btn-primary"
                >
                  {isProcessing ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowManagement; 